import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';

import { bookingShape, clientShape, userShape, historyShape } from 'constants/propTypes';
import { Page, Row, Column } from 'components/shared/Layout';
import { defaultClientData } from 'store/client/state';
import { bookingStatus } from 'types/booking';
import { getJourneysWithFilteredBookingItems } from 'utils/exchange';
import { getBookingAttachedLoyaltyAccount } from 'utils/booking';
import filterLegsByProductItemRefs from 'utils/filterLegsByProductItemRefs';

import { GET_CUSTOMER_DETAILS } from 'store/queries';
import AgentPortalError from 'errors/AgentPortalError';
import { ACTION_CODES } from 'constants/errors';
import ApolloErrorSection from 'components/errors/ApolloErrorSection';

import { isEmpty } from 'ramda';
import { productFamilyCodes } from 'types/productFamilyCodes';
import { Calendar } from 'react-feather';
import { Grid, AvailablePointsCell, AvailablePointsBoldCell, ClubEurostarImg } from '../../JourneySearch/JourneySearch.styles';
import ExchangeJourneySelection from './ExchangeJourneySelection';
import { BackButton, StyledToggle } from './ExchangeDetails.styles';

import { isBookingMultiJourney } from 'components/ui/BookingPathReviewBookingSummary/BookingPathReviewBookingSummary.utils';
import { isLoyaltyTier3OrAbove } from 'utils/loyaltyData';
import { getPriorityLeg } from '../Exchange.utils';

const initialState = {
  isMember: false,
  activePoints: 0,
  memberNotFoundError: {},
  showBookingMagnet: false,
  hasSpecialLoyaltyTier: false
};

const ExchangeDetails = ({ booking, reference, bookingItemRefs, client, overrideReason, exchangeCurrency, membershipNumber, history }) => {
  const [state, setState] = useState(initialState);

  useEffect(() => {
    (async () => {
      const attachedMembershipNumber = getBookingAttachedLoyaltyAccount(booking);
      const isWrongAccountLoggedIn = attachedMembershipNumber && membershipNumber && membershipNumber !== attachedMembershipNumber;

      client.writeQuery({
        query: gql`
          query {
            billingDetails
          }
        `,
        data: { billingDetails: defaultClientData.billingDetails }
      });

      if (membershipNumber) {
        if (isWrongAccountLoggedIn) {
          setState((prev) => ({
            ...prev,
            memberNotFoundError: new Error('Booking is not linked to this loyalty account')
          }));

          return;
        }
        try {
          const result = await client.query({
            query: GET_CUSTOMER_DETAILS,
            variables: {
              membershipNumber
            },
            fetchPolicy: 'network-only'
          });

          const customerInfo = result?.data?.getCustomerDetails;
          if (customerInfo?.profile?.loyalty) {
            setState((prev) => ({
              ...prev,
              isMember: true,
              activePoints: customerInfo.profile.loyalty?.rewardPoints,
              hasSpecialLoyaltyTier: isLoyaltyTier3OrAbove(customerInfo.profile.loyalty.tier.tierCode)
            }));
          }
        } catch (err) {
          setState((prev) => ({
            ...prev,
            memberNotFoundError: err
          }));
        }
      }
    })();
  }, []);

  const toggleShowBookingMagnet = () =>
    setState((prev) => ({
      ...prev,
      showBookingMagnet: !prev.showBookingMagnet
    }));

  const setShowBookingMagnet = (nextShowBookingMagnet) =>
    setState((prev) => ({
      ...prev,
      showBookingMagnet: nextShowBookingMagnet
    }));

  const { inbound, outbound, displayStatus, productFamilies, outboundExtras, inboundExtras } = booking;

  const isMultiJourneyBooking = isBookingMultiJourney(booking);

  const { isMember, activePoints, memberNotFoundError, showBookingMagnet, hasSpecialLoyaltyTier } = state;
  const outboundLegs = filterLegsByProductItemRefs(outbound, bookingItemRefs);
  const inboundLegs = filterLegsByProductItemRefs(inbound, bookingItemRefs);
  const isLoyaltyBooking = productFamilies?.some((pf) => pf.familyGroup === productFamilyCodes.EUROSTAR_LOYALTY);

  const outboundTrainNumbers = outboundLegs.length > 0 ? outboundLegs.map((leg) => leg.trainNumber) : [];
  const inboundTrainNumbers = inboundLegs.length > 0 ? inboundLegs.map((leg) => leg.trainNumber) : [];

  const [priorityOutboundLeg] = getPriorityLeg(outboundLegs);
  const [priorityInboundLeg] = getPriorityLeg(inboundLegs);

  const journeys = getJourneysWithFilteredBookingItems(
    {
      outbound: priorityOutboundLeg,
      inbound: priorityInboundLeg,
      displayStatus
    },
    bookingItemRefs,
    !!overrideReason
  );

  const { ON_HOLD, TEMPORARILY_ON_HOLD, PROVISIONAL } = bookingStatus;
  const isOnHoldOrProvisional = [ON_HOLD, TEMPORARILY_ON_HOLD, PROVISIONAL].includes(displayStatus);
  const showExchangeJourneySelection = isOnHoldOrProvisional
    ? (journeys.outbound.activeBookingItems && journeys.outbound.activeBookingItems.length > 0) ||
      (journeys.inbound.activeBookingItems && journeys.inbound.activeBookingItems.length > 0)
    : journeys.outbound.bookingItems.length > 0 || journeys.inbound.bookingItems.length > 0;

  const swapStationUrl = new URLSearchParams(history?.location?.search).get('swapStations');

  const attachedMembershipNumber = getBookingAttachedLoyaltyAccount(booking);
  const isWrongAccountLoggedIn = attachedMembershipNumber && membershipNumber && membershipNumber !== attachedMembershipNumber;
  const searchMenuTextId = showBookingMagnet ? 'exchange.hideBookingMagnet' : 'exchange.showBookingMagnet';
  const membershipNumberUrl = membershipNumber ? `?membershipNumber=${membershipNumber}` : '';

  return (
    <Page overflow="hidden">
      <Row>
        <Column start={1} span={7}>
          <BackButton element={<Link to={`/manage/bookings/${reference}/journeys${membershipNumberUrl}`} />}>
            <FormattedMessage id="exchanges.back" />
          </BackButton>
        </Column>
        <Column span={3} start={14} justifyContent="flex-end">
          {showExchangeJourneySelection && (
            <StyledToggle onClick={toggleShowBookingMagnet} id="toggle-search">
              <Calendar />
              <FormattedMessage id={searchMenuTextId} />
            </StyledToggle>
          )}
        </Column>
      </Row>
      {!isEmpty(memberNotFoundError) && (
        <ApolloErrorSection
          error={
            new AgentPortalError(memberNotFoundError, ACTION_CODES.REDIRECT_TO_CLUB_EUROSTAR, {
              isWrongAccountLoggedIn
            })
          }
        />
      )}

      {isMember && isLoyaltyBooking && (
        <Row span={12}>
          <Grid>
            <AvailablePointsCell>
              <ClubEurostarImg />
            </AvailablePointsCell>
            <AvailablePointsCell>
              <FormattedMessage id="clubEurostar.activePoints" />
            </AvailablePointsCell>
            <AvailablePointsBoldCell>{activePoints}</AvailablePointsBoldCell>
          </Grid>
        </Row>
      )}

      {showExchangeJourneySelection || swapStationUrl === 'true' ? (
        <ExchangeJourneySelection
          exchangeCurrency={exchangeCurrency}
          booking={booking}
          reference={reference}
          outbound={journeys.outbound}
          inbound={journeys.inbound}
          outboundTrainNumbers={outboundTrainNumbers}
          inboundTrainNumbers={inboundTrainNumbers}
          overrideReason={overrideReason}
          activePoints={activePoints}
          isMember={isMember}
          hasSpecialLoyaltyTier={hasSpecialLoyaltyTier}
          membershipNumber={membershipNumber}
          showBookingMagnet={showBookingMagnet}
          setShowBookingMagnet={setShowBookingMagnet}
          outboundExtras={outboundExtras?.filter((e) => !e.cancelled)}
          inboundExtras={inboundExtras?.filter((e) => !e.cancelled)}
          isMultiJourneyBooking={isMultiJourneyBooking}
          bookingItemRefs={bookingItemRefs}
          outboundLegs={outboundLegs}
          inboundLegs={inboundLegs}
        />
      ) : (
        <h1>
          <FormattedMessage id="exchanges.noExchanges" />
        </h1>
      )}
    </Page>
  );
};

export default ExchangeDetails;

ExchangeDetails.propTypes = {
  user: userShape,
  booking: bookingShape,
  reference: PropTypes.string,
  bookingItemRefs: PropTypes.arrayOf(PropTypes.string),
  client: clientShape,
  overrideReason: PropTypes.string,
  exchangeCurrency: PropTypes.string.isRequired,
  changeCurrency: PropTypes.func.isRequired,
  membershipNumber: PropTypes.string,
  history: historyShape
};
